import { type ReactNode } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { triggerHaptic } from '@repo/shared/client';
import { Anchor } from '~/components/ui';
import { useAuthForm } from '~/lib/hooks/use-auth-form';
import { AuthForm } from './AuthForm';

// Optional `?email=` prefill — the "sign in instead" link on the duplicate-email
// sign-up error deep-links here with the address the user just typed.
const SignInSearchSchema = z.object({
  email: z.string().email().optional().catch(undefined),
});
type SignInSearch = z.infer<typeof SignInSearchSchema>;

export const Route = createFileRoute('/_auth/sign-in')({
  validateSearch: (search): SignInSearch => SignInSearchSchema.parse(search),
  component: SignInRoute,
});

function SignInRoute(): ReactNode {
  const { t } = useTranslation();
  const { email } = Route.useSearch();
  const form = useAuthForm('sign-in', '', email ?? '');

  return (
    <AuthForm
      mode="sign-in"
      {...form}
      forgotPasswordLink={
        <Anchor
          component={Link}
          to="/forgot-password"
          size="sm"
          data-testid="sign-in-forgot-password-link"
          onClick={() => triggerHaptic()}
        >
          {t('auth.forgotPassword')}
        </Anchor>
      }
      alternateLink={
        <Anchor
          component={Link}
          to="/sign-up"
          size="sm"
          data-testid="sign-in-sign-up-link"
          onClick={() => triggerHaptic()}
        >
          {t('nav.signUp')}
        </Anchor>
      }
    />
  );
}
